/**
 * What a new company starts with, and the choices it is offered.
 *
 * The single-tenant implementation had the rupee, Indian digit grouping,
 * `en-IN` and `Asia/Kolkata` spread across twenty-five files, which is why
 * making it multi-company meant touching every one of them. Here they exist in
 * exactly one place, they are only *defaults*, and the moment a company is
 * created they become values on that company's row — read from there by every
 * screen, never from this file.
 *
 * `pnpm check:tenant` fails the build if these literals appear anywhere else in
 * the client. That is the point of the rule: a default is fine, a constant is
 * not.
 */

export interface WorkspaceDefaults {
    country: string;
    currency: string;
    timezone: string;
    locale: string;
    phoneCountry: string;
}

/**
 * India, because that is where the first customers are. Every one of these is
 * changeable on the form that creates the company, and in settings afterwards.
 */
export const DEFAULT_WORKSPACE: WorkspaceDefaults = {
    country: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    locale: 'en-IN',
    phoneCountry: '91',
};

/**
 * The short lists the setup form offers.
 *
 * Deliberately short. A company that needs something not here can have it —
 * the columns are plain text and settings will take any valid value — but a
 * dropdown of four hundred time zones is a worse first five minutes than eight
 * likely ones and a way to change it later.
 */
export const CURRENCY_CHOICES = ['INR', 'GBP', 'USD', 'EUR', 'AED', 'AUD', 'CAD', 'SGD'] as const;

export const TIMEZONE_CHOICES = [
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Europe/London',
    'Europe/Berlin',
    'America/New_York',
    'America/Los_Angeles',
    'Australia/Sydney',
    'UTC',
] as const;

export const LOCALE_CHOICES = [
    'en-IN', 'en-GB', 'en-US', 'en-AE', 'en-SG', 'en-AU', 'de-DE', 'fr-FR',
] as const;
